import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import requestDocumentsPageStyle from "./requestDocumentsPageStyle";
import Paper from "@mui/material/Paper";
import ApiClient from "@/src/services/apiClient";
import { useNavigate } from "react-router";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

const RequestDocumentsPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("traffic", "apiUri");
  const { data: traffic, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }
  return <RequestDocumentsPageInner traffic={traffic} />;
};

type RequestDocumentsPageInnerProps = {
  traffic: AbstractCase;
};

const RequestDocumentsPageInner = ({ traffic }: RequestDocumentsPageInnerProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);

  let mediaObjectCombinationRequestTypes = [];
  if (traffic.fineNotice === null) {
    mediaObjectCombinationRequestTypes.push("files");
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction } = useMediaObjectCombinationRequest(
    traffic,
    mediaObjectCombinationRequestTypes,
  );

  const navigate = useNavigate();
  const triggerUpdate = async () => {
    setIsLoadingInternal(true);
    await ApiClient.post(`/generate_requested_mediaobjects/traffic/${traffic.id}`);
    navigate(getCaseLink(traffic));
  };

  useEffect(() => {
    if (traffic) {
      let hasRequestedDocument = traffic.fineNotice === null;
      if (!hasRequestedDocument) {
        navigate(getCaseLink(traffic));
      }
    }
  }, [traffic, navigate]);

  if (isLoadingInternal || isLoadingMediaObjectCombinationRequest) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Fehlende Dokumente | Legalbird</title>
      </Helmet>
      <ContentBox headline={<strong>Fehlende Dokumente ergänzen</strong>} headlineVariant={"h5"} showDivider={true}>
        Um Ihren Fall weiter bearbeiten zu können, benötigen wir noch folgende Unterlagen von Ihnen.
      </ContentBox>
      <Paper elevation={4} sx={requestDocumentsPageStyle.box}>
        {traffic.fineNotice === null && (
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests.files}
            temporaryMediaObjectsFieldname={"files"}
            headline={"Bußgeldbescheid"}
            helperText={"Bitte laden Sie Ihren Bußgeldbescheid hoch."}
            productEntity={traffic}
            productEntityId={traffic.id}
            product={"traffic"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            fieldName={"fineNotice"}
          />
        )}
        <br />
        <br />
        <ButtonLoading
          onClick={triggerUpdate}
          color="primary"
          variant="contained"
          disabled={false}
          fullWidth={true}
          type={"submit"}
          isLoading={isLoadingInternal}
        >
          Dokumente hochladen
        </ButtonLoading>
      </Paper>
    </LayoutProtectedRoutes>
  );
};

export default RequestDocumentsPage;
